import { faCircleNodes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThinkingIcon } from 'components/thinking_icon/ThinkingIcon';
import { forwardRef, useMemo } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { useIsDeskChatUser } from 'utils/useIsDeskChatUser';

export const AvatarDefaultSize = '4rem';
export const AvatarDefaultFontSize = '1.6rem';

export const Avatar = styled.div<{
    $backgroundColor: string;
    $color: string;
    $borderColor?: string;
    $fontSize: string;
    $size: string;
}>`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: ${({ $color }) => $color};
    width: ${({ $size }) => $size};
    height: ${({ $size }) => $size};
    font-size: ${({ $fontSize }) => $fontSize};
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    border: ${({ $borderColor }) =>
        $borderColor ? `1.5px solid ${$borderColor}` : 'none'};
`;

const hRange = [0, 360];
const sRange = [50, 75];
const lRange = [25, 60];

type HSL = number[];

const getHashOfString = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);
    return hash;
};

const normalizeHash = (hash: number, min: number, max: number) =>
    Math.floor((hash % (max - min)) + min);

const generateHSL = (name: string): HSL => {
    const hash = getHashOfString(name);
    const h = normalizeHash(hash, hRange[0], hRange[1]);
    const s = normalizeHash(hash, sRange[0], sRange[1]);
    const l = normalizeHash(hash, lRange[0], lRange[1]);
    return [h, s, l];
};

const HSLtoString = (hsl: HSL) => `hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`;

type UserAvatarProps = Readonly<{
    userId: string | undefined;
    firstName: string | undefined;
    lastName: string | undefined;
    isCmms?: boolean;
    fontSize?: string;
    size?: string;
    className?: string;
    bordered?: boolean;
    animated?: boolean;
}>;

export const UserAvatar = forwardRef<HTMLDivElement, UserAvatarProps>(
    (
        {
            userId,
            firstName,
            lastName,
            isCmms = false,
            fontSize = AvatarDefaultFontSize,
            size = AvatarDefaultSize,
            className,
            bordered,
            animated = false,
        },
        ref,
    ) => {
        const isDeskChatUser = useIsDeskChatUser();
        const backgroundColor = useMemo(
            () => HSLtoString(generateHSL(`${firstName} ${lastName}`)),
            [firstName, lastName],
        );

        if (isDeskChatUser(userId)) {
            return (
                <ThinkingIcon
                    ref={ref}
                    size={size}
                    className={className}
                    animated={animated}
                />
            );
        }

        return (
            <Avatar
                ref={ref}
                className={className}
                $backgroundColor={backgroundColor}
                $fontSize={fontSize}
                $size={size}
                $color={tokens.text.onColor}
                $borderColor={bordered ? tokens.text.onColor : undefined}
            >
                {isCmms ? (
                    <FontAwesomeIcon icon={faCircleNodes} />
                ) : (
                    <>
                        {firstName?.charAt(0).toUpperCase() ?? 'U'}
                        {lastName?.charAt(0).toUpperCase() ?? 'U'}
                    </>
                )}
            </Avatar>
        );
    },
);
